import React, { useState, useEffect, useCallback } from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Typography, CircularProgress, TextField, Button, InputAdornment } from '@mui/material';
import { Search, Clear } from '@mui/icons-material';
import ApiServices from '../../services/ApiServices';
import { DATA_COLUMNS } from './HardwareConstants';

const HardwareMismatchTable = ({ refreshKey }) => {
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [imeiSearch, setImeiSearch] = useState('');
    const [appliedImeis, setAppliedImeis] = useState('');

    const fetchMismatchedHardware = useCallback(async () => {
        setLoading(true);
        const params = new URLSearchParams({ page, size: rowsPerPage });
                
        if (appliedImeis.trim()) {
            const imeiList = appliedImeis.split(',').map(i => i.trim()).filter(i => i.length > 0);
            imeiList.forEach(imei => params.append('imeiList', imei));
        }
        try {
            const response = await ApiServices.getMismatchedHardware(params.toString());
            if (response.status === 200) {
                setTableData(response.data.content || response.data);
                setTotalRows(response.data.totalElements || 0);
            }
        } catch (error) {
            console.error("Error fetching hardware:", error);
        }
        setLoading(false);
    }, [page, rowsPerPage, appliedImeis]);

    useEffect(() => {
        fetchMismatchedHardware();
    }, [fetchMismatchedHardware, refreshKey]);

    const handleSearch = () => {
        setPage(0);
        setAppliedImeis(imeiSearch);
    };

    const handleClear = () => {
        setImeiSearch('');
        setAppliedImeis('');
        setPage(0);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    return (
        <TableContainer component={Paper} elevation={3} sx={{ p: 1, height: 'auto', width: '100%', overflowX: 'auto' }}>
            <Box p={1}>
                <Typography variant="h6">Hardware Mismatches</Typography>
            </Box>
                        
            <Box display="flex" gap={1} alignItems="center" px={1} pb={2}>
                <TextField
                    label="Search IMEIs"
                    placeholder="Enter comma separated IMEIs"
                    value={imeiSearch}
                    onChange={(e) => setImeiSearch(e.target.value)}
                    onKeyPress={handleKeyPress}
                    size="small"
                    sx={{ minWidth: 300 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button 
                    variant="contained" 
                    onClick={handleSearch}
                    size="medium"
                >
                    Search
                </Button>
                {appliedImeis && (
                    <Button 
                        variant="outlined" 
                        onClick={handleClear}
                        size="medium"
                        startIcon={<Clear />}
                    >
                        Clear
                    </Button>
                )}
            </Box>
            {loading ? (
                <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>
            ) : (
                <>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                {DATA_COLUMNS.map((col) => <TableCell key={col.key} sx={{ fontWeight: 'bold' }}>{col.label}</TableCell>)}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableData.length > 0 ? tableData.map((row, idx) => (
                                <TableRow key={row.imei || idx}>
                                    {DATA_COLUMNS.map((col) => <TableCell key={col.key}>{String(col.access(row))}</TableCell>)}
                                </TableRow>
                            )) : (
                                <TableRow><TableCell colSpan={DATA_COLUMNS.length} align="center">No records found.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={totalRows}
                        page={page}
                        onPageChange={(e, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                    />
                </>
            )}
        </TableContainer>
    );
};

export default HardwareMismatchTable;